// Address autocomplete via OpenStreetMap Nominatim.
// Free, no API key required. Polite use requires a User-Agent + max 1 req/sec
// which we enforce via a small debounce.
import { useEffect, useState, useRef } from "react";
import { MapPin, Loader2 } from "lucide-react";

const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";
// Default bias to Chile so suggestions surface chilean addresses first.
const COUNTRY_BIAS = "cl";

export default function AddressAutocomplete({
  value,
  onChange,
  placeholder = "Ej: Av. Concha y Toro 123, Puente Alto",
  testid = "address-input",
  disabled = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // Debounced fetch when value changes
  useEffect(() => {
    if (!value || value.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          q: value, format: "json", addressdetails: "1", limit: "6",
          countrycodes: COUNTRY_BIAS, "accept-language": "es",
        });
        const r = await fetch(`${NOMINATIM_URL}?${params.toString()}`, {
          headers: { Accept: "application/json" },
        });
        if (!r.ok) throw new Error("nominatim error");
        const data = await r.json();
        setSuggestions(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch {
        setSuggestions([]);
      } finally { setLoading(false); }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value]);

  const pick = (s) => {
    onChange(s.display_name);
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <MapPin size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A] pointer-events-none"/>
        <input
          type="text"
          className="input-tactical w-full pl-8"
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={testid}
          autoComplete="off"
        />
        {loading && (
          <Loader2 size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] animate-spin"/>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-50 top-full left-0 right-0 mt-1 bg-[#0A0A0A] border border-[#27272A] max-h-72 overflow-y-auto shadow-xl"
          data-testid={`${testid}-suggestions`}
        >
          {suggestions.map((s, i) => (
            <button
              key={`${s.place_id}-${i}`}
              type="button"
              onClick={() => pick(s)}
              className="w-full text-left px-3 py-2.5 text-xs text-[#A1A1AA] hover:bg-[#1A1A1A] hover:text-white border-b border-[#27272A] last:border-b-0 flex items-start gap-2"
              data-testid={`${testid}-suggestion-${i}`}
            >
              <MapPin size={11} className="text-[#71717A] mt-0.5 shrink-0"/>
              <span className="leading-tight">{s.display_name}</span>
            </button>
          ))}
          <div className="px-3 py-1.5 text-[9px] uppercase tracking-widest text-[#52525b] bg-[#0F0F0F] border-t border-[#27272A]">
            Sugerencias · OpenStreetMap
          </div>
        </div>
      )}
    </div>
  );
}
